// janus-engine/inventory/payload-chain.ts — R1b-2d (Janus round 4, session 4): a destruction payload's WHOLE CHAIN, decided in one pass.
//
// PURE. Legacy (includes/functions/janus/items/environmental_payload_dispatcher.php + inventory_destruction.php):
//   · ft_drain_environmental_payloads() walks the queue FIFO; ft_resolve_environmental_payload() runs one job's abilities (surfaces before
//     bursts) through janus() in trigger mode on the LIVE encounter;
//   · a burst that brings an object to 0 V destroys it INLINE (apply_damage_to_target → handle_inventory_destroyed_from_damage()): its
//     rows land right after that target's damage row, the row leaves the live roster (update_inventory_stats inventory_deleted=1) and its
//     pile joins it (add_inventory) — so a later step, or a later job, collects the pile and never the dead row — and its OWN payload is
//     enqueued (roll group = the parent's, breaking types = ft_damage_types_in_roll of the damage that broke it), to run after every job
//     already queued.
// This module is that walk with every dependency injected: the step runner (Lane A's runEnvironmentalPayload on ONE context, so a later
// job sees an earlier one's zones, statuses and damage), the caused-destruction resolver (R1's destroy-object resolver on the working
// roster) and the frame applier. It returns every job's verdicts and every caused destruction in legacy's order — the caller dry-runs it
// on synthetic dice first, then draws for real, then commits (pre-draw fairness: every hand-back is decided before a real die).
import { planPayloadDrain, PAYLOAD_MAX_RESOLUTIONS, type DestructionPayloadJob, type PayloadReaders, type PayloadStep } from './destruction-payload.js';
import type { ObjectDestroyCommand, ObjectDestroyFrame } from './destroy-object.js';

export type ResolveStep = Extract<PayloadStep, { action: 'resolve' }>;

/** The slice of a step verdict the chain reads (Lane A's PayloadVerdict satisfies it). */
export interface ChainStepVerdict {
  rows: unknown[];
  objectDamage: Array<{ inventory_id: string; destroyed: boolean; damageTypeIds: string[]; afterRow: number; total: number }>;
}
/** One destruction a step caused: which job / step, where its rows go (after row `afterRow` of that step), and its command. */
export interface ChainCaused { job: number; step: number; afterRow: number; inventoryId: string; command: ObjectDestroyCommand }
export interface ChainJob<V extends ChainStepVerdict = ChainStepVerdict> { job: DestructionPayloadJob; steps: ResolveStep[]; verdicts: V[] }
export type PayloadChainResult<V extends ChainStepVerdict = ChainStepVerdict> =
  | { ok: true; jobs: ChainJob<V>[]; caused: ChainCaused[] }
  | { ok: false; reason: string; fetch?: string[] };
export type CausedResolution = ObjectDestroyCommand | { reason: string } | { fetch: string[] };

export interface PayloadChainDeps<V extends ChainStepVerdict> {
  read: PayloadReaders;
  /** Job-level static gates (an anchor the commit cannot take, surface chemistry …): a reason hands the whole chain back. */
  gate?: (job: DestructionPayloadJob, steps: ResolveStep[]) => string | null;
  /** Run ONE resolve step on the shared context (throws HandBack / Refusal like janus()). */
  runStep: (job: DestructionPayloadJob, step: ResolveStep) => V;
  /** The destruction a step caused, resolved on the working roster as it stands (the step's earlier caused frames applied). */
  resolveCaused: (job: DestructionPayloadJob, step: ResolveStep, entry: V['objectDamage'][number]) => CausedResolution;
  /** Apply a caused destruction's frame to every roster the runner reads (the object leaves, its pile arrives). */
  apply: (frame: ObjectDestroyFrame) => void;
}

const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();

/**
 * D-048 (owner 2026-09-30: "resolve every chained-destruction shape in the browser; no PHP fallback"): the chain's SHAPE — which objects a
 * draw destroys, so which caused destructions resolve and which jobs run — depends on the dice. The page decides every shape a draw could
 * reach BEFORE it draws, by walking three worlds:
 *   · `natural`  — legacy's own walk: an object is destroyed when the step's verdict says so;
 *   · `forced`   — every object a damaging step actually HIT (damage > 0) is destroyed at that hit: the closure of every caused destruction,
 *                  every pile / spill it leaves and every payload job it could enqueue;
 *   · `isolated` — one job alone (its caused destructions resolved and applied, nothing enqueued) on a roster holding every row any world
 *                  could put on the floor: a job that only runs in some worlds meets targets another world left standing.
 * A hand-back in any world is decided pre-draw; the real draw then walks `natural` and can only meet shapes already resolved.
 */
export type ChainWorld = 'natural' | 'forced' | 'isolated';

/**
 * ft_drain_environmental_payloads() over `root` and everything it causes. A plan asking for definitions returns `fetch` (the caller
 * fetches, then runs the whole chain again); any gate / resolver reason stops the walk. PHP drops what is left after
 * PAYLOAD_MAX_RESOLUTIONS jobs with a warning a page cannot show — that hands back too. `root` may be several jobs (a plain destruction whose
 * spill's splash destroyed payload children queues theirs, in legacy order).
 */
export function runPayloadChain<V extends ChainStepVerdict>(root: DestructionPayloadJob | readonly DestructionPayloadJob[], deps: PayloadChainDeps<V>,
  world: ChainWorld = 'natural'): PayloadChainResult<V> {
  const queue: DestructionPayloadJob[] = Array.isArray(root) ? [...root] : [root as DestructionPayloadJob];
  const jobs: ChainJob<V>[] = [];
  const caused: ChainCaused[] = [];
  for (let i = 0; i < queue.length; i++) {
    if (i >= PAYLOAD_MAX_RESOLUTIONS) return { ok: false, reason: 'payload-budget-exhausted' };
    const job = queue[i]!;
    const plan = planPayloadDrain(deps.read, job);
    if ('reason' in plan) return { ok: false, reason: plan.reason, ...(plan.definitionIds?.length ? { fetch: plan.definitionIds } : {}) };
    const steps = plan.steps.filter((step): step is ResolveStep => step.action === 'resolve');
    const gated = deps.gate?.(job, steps) ?? null;
    if (gated) return { ok: false, reason: gated };
    const run: ChainJob<V> = { job, steps, verdicts: [] };
    jobs.push(run);
    for (let s = 0; s < steps.length; s++) {
      const verdict = deps.runStep(job, steps[s]!);
      run.verdicts.push(verdict);
      // In the order the step destroyed them — each one's rows follow its own damage row.
      for (const entry of verdict.objectDamage) {
        if (!entry.destroyed && !(world === 'forced' && entry.total > 0)) continue;
        const resolved = deps.resolveCaused(job, steps[s]!, entry);
        if ('fetch' in resolved) return { ok: false, reason: 'payload-chain-definition-missing', fetch: resolved.fetch };
        if ('reason' in resolved) return { ok: false, reason: `payload-chain:${resolved.reason}` };
        deps.apply(resolved.frame);
        caused.push({ job: jobs.length - 1, step: s, afterRow: entry.afterRow, inventoryId: upper(entry.inventory_id), command: resolved });
        // Round 5 session C: a child its spill's splash destroyed ends its OWN destruction (and enqueues its payload) inside the
        // parent's spill — before the parent reaches its enqueue: nested first, depth-first, then the parent (legacy order)
        if (world !== 'isolated') enqueueCaused(resolved, queue);
      }
    }
  }
  return { ok: true, jobs, caused };
}

/** Round 5 session C: a child its spill's splash destroyed ends its OWN destruction (and enqueues its payload) inside the parent's spill —
 * before the parent reaches its enqueue: nested first, depth-first, then the parent (legacy order). */
export function enqueueCaused(command: ObjectDestroyCommand, queue: DestructionPayloadJob[]): DestructionPayloadJob[] {
  for (const child of command.spill?.destroyed ?? []) enqueueCaused(child, queue);
  if (command.payload) queue.push(command.payload);
  return queue;
}

/** One commit of a step: the step's verdict is posted once per segment; `after` = the caused destructions committed right after it. */
export interface StepSegment { job: number; step: number; index: number; cuts: number[]; after: ChainCaused[] }

/**
 * Legacy's row order: a step whose caused destructions fall BEFORE its last row is cut at each one's afterRow (rows [cut k-1, cut k) per
 * segment), each segment followed by the destructions it caused; a destruction ending the step follows its last segment. The lazy save
 * (destruction_chain_save_inc.php) writes the chain's ops in exactly this order (payload-chain-frame.ts buildChainOps).
 */
export function payloadCommitPlan<V extends ChainStepVerdict>(chain: { jobs: ChainJob<V>[]; caused: ChainCaused[] }): StepSegment[] {
  const out: StepSegment[] = [];
  chain.jobs.forEach((run, j) => run.verdicts.forEach((verdict, s) => {
    const mine = chain.caused.filter(c => c.job === j && c.step === s);
    const cuts = [...new Set(mine.map(c => c.afterRow).filter(a => a > 0 && a < verdict.rows.length))].sort((a, b) => a - b);
    for (let k = 0; k <= cuts.length; k++) out.push({ job: j, step: s, index: k, cuts, after: [] });
    const segments = out.slice(out.length - cuts.length - 1);
    for (const c of mine) {
      const k = c.afterRow < verdict.rows.length ? cuts.indexOf(c.afterRow) : cuts.length;
      const index = k < 0 ? cuts.length : k;
      segments[index]!.after.push(c);
    }
  }));
  return out;
}
